import { Form, Button, Alert } from 'antd';
import BaseModal from 'components/BaseModal'
import { MODAL_SIZE_MEDIUM } from 'constants/Layout';
import _ from 'lodash'
import React, { forwardRef, useState, useImperativeHandle, useRef, useCallback } from 'react'
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { FieldTypes, nameRegexValidator, urlRegexValidator, generateFormFields } from 'utils/formUtils';
import { duplicateNameValidator } from 'utils/validatorUtil';
import ModalUtil from 'utils/modalUtil';
import JiraMappingForm from '../jira/JiraMappingForm';
import message from 'components/CustomMessage';
import SingleCollapsePanel from 'components/SingleCollapsePanel';
import { hasPermission } from 'utils/authService';
import { PermissionSetMap } from 'constants/RBACConstants';
import JiraSecurityToolMappingForm from '../JiraSecurityTool/JiraSecurityToolMappingForm';
import { useAutoFocus } from 'hooks/useFocusHelpers';

const AUTHORIZE_TYPE = {
    BASIC: "BASIC",
    TOKEN: "TOKEN",
    OAUTH: "OAUTH",
    BASIC_PREM: "BASIC_PREM"
}

function JiraLoginConfigForm({name, loginConfigList, handleMapping, isSecurityTool = false, ...props}, ref){
    const isEdit = !_.isUndefined(props.config);
    const [form] = Form.useForm();
    const [auth, setAuth] = useState(isEdit ? jiraLoginType(props.config) : AUTHORIZE_TYPE.OAUTH);
    const [apiToken, setApiToken] = useState("")
    const thisRef = useRef();
    const nameInputFieldRef = useAutoFocus();

    useImperativeHandle(ref,() => ({
        ...thisRef
    }))

    const onRadioChange = useCallback((e) => {
        setAuth(e.target.value);
        form.setFieldsValue({version: [AUTHORIZE_TYPE.BASIC, AUTHORIZE_TYPE.OAUTH, AUTHORIZE_TYPE.BASIC_PREM].includes(e.target.value) ? "V2": "V3"});
    }, [form]);

    const getVerificationCode = useCallback(async()=> {
        await form.validateFields(['url', 'consumerKey', 'privateKey', 'hostUrl'])
        const { url, consumerKey, privateKey, hostUrl } = form.getFieldsValue();
        const { authorizationURL, requestToken, redirectURL } = await ToolsIntegrationsAPI.getJiraAuthURL({ url, consumerKey, privateKey, hostUrl }).promise;
        setApiToken(requestToken);
        window.open(redirectURL || authorizationURL, "_blank", "rel=noopener");
    },[form])

    const jiraLabel = <>
        <span>You can integrate Jira using one of the following options: Basic Auth, Token Auth, OAuth or via Personal Access Token. <br/>
        <strong>For OAuth (Jira On-Prem):</strong> Enter consumer and private key and click on “Allow Access”. A separate tab will be opened where user needs to click on “Allow”, post which a verification code will be generated. Paste the code in the config popup, click on Save.</span>
    </>
    const jiraFields = [
        {
            type: 'Label',
            value: jiraLabel,
        },
        {
            name: 'loginType',
            label: "",
            type: FieldTypes.RADIO,
            options: [{
              value: AUTHORIZE_TYPE.BASIC,
              label: "Basic Auth (Jira On-Prem)",
            },{
              value: AUTHORIZE_TYPE.TOKEN,
              label: "Token Auth (Jira Cloud)"
            },
            {
                value: AUTHORIZE_TYPE.OAUTH,
                label: "OAuth (Jira On-Prem)"
            },
            {
                value: AUTHORIZE_TYPE.BASIC_PREM,
                label: "Personal Access Token (Jira On-Prem)"
            }
        ],
            props: {
              onChange: onRadioChange,
            },
        },
        {
        name: "name",
        label: "Name",
        props: {
            ref: nameInputFieldRef
        },
        colSpan: 12,
        rules: [
            {
                required: true,
                message: "Name is required"
            },
            duplicateNameValidator({ list: loginConfigList }),
            nameRegexValidator
        ],
     }, {
        name: "url",
        label: "URL",
        colSpan: 24,
        required: true,
        rules: [urlRegexValidator()]
    },
    ...([AUTHORIZE_TYPE.BASIC, AUTHORIZE_TYPE.BASIC_PREM].indexOf(auth) !== -1
        ?
        [{
            name: "userName",
            label: "User Name",
            placeholder: "User Name",
            colSpan: 12,
            required: true
        },
        {
            name: "apiToken",
            label: auth === AUTHORIZE_TYPE.BASIC ? "Password": "Personal Access Token",
            type: FieldTypes.PASSWORD,
            colSpan: 12,
            required: true
        }]
        :
        []
    ),
    ...(auth === AUTHORIZE_TYPE.TOKEN 
        ? 
        [{
            name: "email",
            label: "Email",
            colSpan: 12,
            required: true,
            rules: [{
                type: "email",
                message: "Invalid email"
            }]
        },
        {
            name: "apiToken",
            label: "API Token",
            type: FieldTypes.PASSWORD,
            colSpan: 12,
            required: (isEdit) ? false : true,
        }]
        :
        []
    ),
    ...(auth === AUTHORIZE_TYPE.OAUTH
        ? 
        [{
            name: "consumerKey",
            label: "Consumer key",
            colSpan: 12,
            required: true,
        },
        {
            name: "privateKey",
            label: "Private Key",
            type: FieldTypes.PASSWORD,
            colSpan: 12,
            required: true
        },
        {
            type: "Custom",
            render: () => <Button className="pull-right" onClick={getVerificationCode} type='primary'>Allow Access</Button>,
            colSpan: 24,
        }]
        :
        []
    ),
    {
        name: "version",
        label: "API Version",
        colSpan: 12,
        disabled: true
    },
    ...(auth === AUTHORIZE_TYPE.OAUTH
        ? 
        [{
            name: "verificationCode",
            label: "Verification Code",
            type: FieldTypes.PASSWORD,
            colSpan: 12,
            required: true
        }]
        :
        []
    ),
    ...([AUTHORIZE_TYPE.BASIC, AUTHORIZE_TYPE.BASIC_PREM, AUTHORIZE_TYPE.OAUTH].indexOf(auth) !== -1
        ? 
        [{
            name: "advancedConfiguration",
            render: () => {
                return <AdvancedConfigSection form={form} />
            }
        }]
        :
        [])
    ]
    const onOk = async () => {
        await form.validateFields();
        try{
            const toolName = name.toUpperCase().replace(' ', '_');
            let payload = form.getFieldsValue();
            if(auth === AUTHORIZE_TYPE.OAUTH){
                payload = {
                    ...payload,
                    apiToken,
                    tokenType: "ACCESS_TOKEN"
                }
            }
            if(auth === AUTHORIZE_TYPE.BASIC_PREM){
                payload = {
                    ...payload,
                    loginType: "BASIC",
                    tokenType: "PERSONAL_ACCESS_TOKEN"
                }
            }
            if (auth === AUTHORIZE_TYPE.TOKEN) {
                payload = {
                    ...payload,
                    apiToken: payload.apiToken === undefined ? "" : payload.apiToken,
                    loginType: "TOKEN",
                }
            }

            if(isSecurityTool){
                payload = _.omit({...payload, apiEndpoint: payload.url}, ["url"])
            }

            let response;
            if(isSecurityTool){
              response = await ToolsIntegrationsAPI[!isEdit ? "postGenericLoginDetail" : "updateGenericLoginDetail"](payload, "Jira", props.config?.id).promise;
            } else {
                response = await ToolsIntegrationsAPI[!isEdit ? "postTicketSystemToolLoginDetails" : "updateTicketSystemToolLoginDetails"](toolName, payload, props.config?.id).promise;
            }

            message.success(`${name} Login Configuration created`)
            if(!isEdit){
                const MappingFormComponent = isSecurityTool ? JiraSecurityToolMappingForm : JiraMappingForm;
                if (_.isFunction(handleMapping)) {
                    handleMapping(response);
                } else {
                    ModalUtil.show({
                        content: <MappingFormComponent onUpdateConfigComponent={props.onUpdateConfigComponent} name={name} loginConfig={response} loginConfigId={response.id} {...props}/>
                    })
                }
            }
            props.onUpdateConfigComponent && props.onUpdateConfigComponent(true);
        }catch(err){
            console.log(err);
            if(!_.isEmpty(err.response.data.errors)){
                const {field, defaultMessage} = err.response.data.errors[0]
                form.setFields([
                    {
                      name: field,
                      errors: [defaultMessage],
                    },
                ]);   
            }
            return Promise.reject(err.response?.data?.message);
        }
    };

    function getFields(name) {
        let newFields = []
        switch(name){
            case "Jira":
                newFields = jiraFields;
                break;
            default:
                return newFields
        }
        const [field] = newFields;
        field.props = {...(field.props || {}), autoFocus: true }
        return newFields
    }

    const modalProps = {
        title: <>{isEdit ? "Edit" : "Add"} {name} Login Credentials </>,
        width: MODAL_SIZE_MEDIUM,
        disabled: !hasPermission(isEdit ? PermissionSetMap.EDIT_TOOL_CONFIG : PermissionSetMap.WRITE_TOOLS),
        onOk: onOk
    }

    return (
        <BaseModal
            modalProps={(_.isNull(apiToken) && auth === AUTHORIZE_TYPE.OAUTH) ? {...modalProps, disabled: true} : modalProps}
            ref={thisRef}
            afterClose={props.afterClose}
        >  
            <Form
                initialValues={isEdit ? {...props.config, loginType: jiraLoginType(props.config)} : {loginType: AUTHORIZE_TYPE.OAUTH, version: [AUTHORIZE_TYPE.BASIC, AUTHORIZE_TYPE.OAUTH, AUTHORIZE_TYPE.BASIC_PREM].includes(auth) ? "V2": "V3"}}
                form={form}
                layout="vertical"
            >
                {generateFormFields(getFields(name))}
            </Form>
        </BaseModal>
    )
}

export default forwardRef(JiraLoginConfigForm);

const jiraLoginType = (config) => {
    const {
      loginType,
      tokenType
    } = config;

    if (_.isNull(tokenType)) return loginType;
    if (tokenType === 'ACCESS_TOKEN') {
      return AUTHORIZE_TYPE.OAUTH;
    } else if (tokenType === 'PERSONAL_ACCESS_TOKEN') {
      return AUTHORIZE_TYPE.BASIC_PREM;
    }
    return AUTHORIZE_TYPE.BASIC;
  }

  const configHostUrlField = [{
    name: 'hostUrl',
    colSpan: 24,
    label: "Customize JIRA Host URL",
    rules: [urlRegexValidator()],
    help: 'When using a proxy to connect to JIRA, this field can be used to update the ticket links on ArmorCode to the actual internal JIRA host. Ex: when ArmorCode connects to your on-premise JIRA via proxy URL https://ext-jira.company.com(configured above), which in turn proxies the request to https://int-jira.company.com(configured here). ArmorCode will open linked tickets on https://int-jira.company.com from its UI'
}]

  export function AdvancedConfigSection({form, value }) {
    return (
        <>
            <SingleCollapsePanel header="Advanced Configuration" className='m-b-xs'>
                <Alert
                    description={(
                        generateFormFields(configHostUrlField)
                    )}
                    type="info"
                    style={{ margin: -16 }}
                />
            </SingleCollapsePanel>
        </>
    )
}
